/** Installed-app payloads from `list_apps` (PLAN panel launcher). */

const ICON_MIMES = new Set(["image/png", "image/webp", "image/jpeg"]);
/** Guard against a runaway payload making a data URL the browser will choke on. */
const MAX_ICON_B64 = 80_000;

export interface InstalledApp {
  package: string;
  label: string;
  icon_b64?: string;
  icon_mime?: string;
}

function parseIcon(item: { icon_b64?: unknown; icon_mime?: unknown }): Pick<InstalledApp, "icon_b64" | "icon_mime"> {
  const raw = item.icon_b64;
  if (typeof raw !== "string" || !raw || raw.length > MAX_ICON_B64) return {};
  const mimeRaw = item.icon_mime;
  const mime = typeof mimeRaw === "string" && ICON_MIMES.has(mimeRaw) ? mimeRaw : "image/png";
  return { icon_b64: raw, icon_mime: mime };
}

/** Prefer labelled launcher entries; fall back to a bare package list. */
export function parseAppList(data: unknown): InstalledApp[] {
  if (!data || typeof data !== "object") return [];
  const rec = data as { apps?: unknown; packages?: unknown };
  if (Array.isArray(rec.apps)) {
    return rec.apps.flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      const row = item as { package?: unknown; label?: unknown; icon_b64?: unknown; icon_mime?: unknown };
      const pkg = row.package;
      if (typeof pkg !== "string" || !pkg) return [];
      const label = row.label;
      return [
        {
          package: pkg,
          label: typeof label === "string" && label ? label : pkg,
          ...parseIcon(row),
        },
      ];
    });
  }
  if (!Array.isArray(rec.packages)) return [];
  return rec.packages.flatMap((pkg) =>
    typeof pkg === "string" && pkg ? [{ package: pkg, label: pkg }] : [],
  );
}

export function filterApps(apps: InstalledApp[], query: string): InstalledApp[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return apps;
  return apps.filter(
    (app) =>
      app.label.toLowerCase().includes(needle) || app.package.toLowerCase().includes(needle),
  );
}
